import PropTypes from 'prop-types';
import phoneIcon from 'public/static/images/dealer_pages/phone.svg';
import emailIcon from 'public/static/images/dealer_pages/email.svg';
import directionIcon from 'public/static/images/dealer_pages/directions.svg';
import websiteIcon from 'public/static/images/dealer_pages/website.svg';
import { colors, screen } from 'components/styles/variables';
import TrackingDataContext from 'contexts/TrackingDataContext';
import { trackDealerPagesClick } from 'lib/Tracking/DealerPages';
import { resolve } from 'styled-jsx/css';
import { useTranslation } from 'react-i18next';
import { useContext } from 'react';
import { DealerPagesEventType } from 'lib/EventSchema';
import Icon from '../../Shared/Icon';
import Action from './Action';

const Dealership = () => {
  const { t } = useTranslation();
  return (
    <span className="dealership">
      {t('dealerPages.dealership')}
      <style jsx>
        {`
          .dealership {
            margin-left: 5px;
            color: ${colors.G4};
            font-weight: 600;
            font-size: 16px;
          }
        `}
      </style>
    </span>
  );
};

const Call = ({ phone }) => {
  const { dealerPageUrl } = useContext(TrackingDataContext);
  const { t } = useTranslation();

  return (
    <a
      className="call"
      href={`tel:${phone}`}
      data-testid="call-link"
      onClick={() =>
        trackDealerPagesClick({
          type: DealerPagesEventType.call_dealership_click,
          dealerPageUrl,
        })
      }
    >
      <Icon image={phoneIcon} />
      <Action>{t('dealerPages.call')}</Action>
      <Dealership />
      <style jsx>{`
        .call {
          display: flex;
          margin-bottom: 15px;
        }
      `}</style>
    </a>
  );
};

Call.propTypes = {
  phone: PropTypes.string.isRequired,
};

const Email = ({ email }) => {
  const { t } = useTranslation();
  const { dealerPageUrl } = useContext(TrackingDataContext);

  return (
    <a
      href={`mailto:${email}`}
      className="email"
      data-testid="email-link"
      onClick={() =>
        trackDealerPagesClick({
          type: DealerPagesEventType.email_click,
          dealerPageUrl,
        })
      }
    >
      <Icon image={emailIcon} />
      <Action>{t('dealerPages.email')}</Action>
      <Dealership />
      <style jsx>{`
        .email {
          display: flex;
          margin-bottom: 15px;
        }
      `}</style>
    </a>
  );
};

Email.propTypes = {
  email: PropTypes.string.isRequired,
};

const Directions = ({ street, city, state, zip }) => {
  const { t } = useTranslation();
  const { dealerPageUrl } = useContext(TrackingDataContext);
  const mapUrl = new URL('https://www.google.com/maps/search/');
  const params = new URLSearchParams('api=1');

  params.append('query', `${street}, ${city}, ${state} ${zip}`);

  mapUrl.search = params.toString();

  return (
    <a
      href={mapUrl.toString()}
      className="dir"
      target="_blank"
      aria-label={t('accessibility.newWindow')}
      rel="nofollow"
      data-testid="directions-link"
      onClick={() =>
        trackDealerPagesClick({
          type: DealerPagesEventType.get_directions_click,
          targetPageUrl: mapUrl.toString(),
          dealerPageUrl,
        })
      }
    >
      <Icon image={directionIcon} />
      <address className="directions">
        <Action>{t('dealerPages.getDirections')}</Action>
        <div className="address">{street}</div>
        <div className="address">{`${city}, ${state} ${zip}`}</div>
      </address>
      <style jsx>{`
        .dir {
          display: flex;
          margin-bottom: 15px;
        }

        .directions {
          display: flex;
          font-style: normal;
          flex-direction: column;
        }

        .address {
          font-size: 16px;
          font-weight: 600;
          color: ${colors.G4};
        }
      `}</style>
    </a>
  );
};

Directions.propTypes = {
  street: PropTypes.string.isRequired,
  city: PropTypes.string.isRequired,
  state: PropTypes.string.isRequired,
  zip: PropTypes.string.isRequired,
};

/* istanbul ignore next */
const websiteActionCSS = resolve`
  .website {
    text-transform: lowercase;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 275px;
  }

  @media (min-width: ${screen.smMin}) {
    .website {
      max-width: 200px;
    }
  }
`;

const Website = ({ site, siteName }) => {
  const { dealerPageUrl } = useContext(TrackingDataContext);
  const { t } = useTranslation();
  return (
    <a
      href={site}
      rel="nofollow"
      className="site"
      target="_blank"
      aria-label={t('accessibility.newWindow')}
      data-testid="website-link"
      onClick={() =>
        trackDealerPagesClick({
          type: DealerPagesEventType.website_click,
          targetPageUrl: site,
          dealerPageUrl,
        })
      }
    >
      <Icon image={websiteIcon} />
      <Action className={`website ${websiteActionCSS.className}`}>
        {siteName}
      </Action>
      <style jsx>{`
        .site {
          display: flex;
          margin-bottom: 15px;
        }
      `}</style>
      {websiteActionCSS.styles}
    </a>
  );
};

Website.propTypes = {
  site: PropTypes.string.isRequired,
  siteName: PropTypes.string.isRequired,
};

export { Call, Email, Directions, Website };
